import moment from "moment-timezone";
import { Link, useParams } from "react-router-dom";
import { SettingsButton } from "src/components/buttons/SettingsButton";
import Breadcrumbs from "src/components/layout/Breadcrumbs";
import Container from "src/components/layout/Container";
import { SettingOnboardsProgress } from "src/components/settings/SettingOnboardProgress";
import { useStore } from "src/stores/Store";
import { useListBackground } from "src/utils/setListBackground";
import { PermissionType } from "types/code-generator";

interface Props {}

export default function DepartmentSettings(props: Props) {
  const store = useStore();
  const match = useParams<{ departmentId: string }>();
  useListBackground();

  const department = store.organization.getDepartment(match.departmentId);

  const canEditDepartment =
    store.user?.permissions?.includes(PermissionType.ManageDepartment) ||
    store.user.isSalesOrAbove;

  const today = moment();
  const departmentUpdated = today.diff(moment(department.updatedAt), "days");

  return (
    <>
      <Container full main className="bg-white">
        <div className="border-b border-borderGray px-4 pb-4">
          <Breadcrumbs>
            <Link to="/admin">Home</Link> /{" "}
            <Link to="/admin/settings">Settings</Link> /{" "}
            <strong>{department.title}</strong>
          </Breadcrumbs>

          <h1 className="mb-0">{department.title}</h1>
        </div>

        <div className="bg-gradient-to-b from-paleAction to-white">
          <Container large className="pb-12 pt-8">
            <SettingOnboardsProgress />

            {canEditDepartment && (
              <div className="mb-8">
                <h2>{department.title}</h2>

                <div className="grid grid-cols-4 gap-4 sm:grid-cols-1">
                  <SettingsButton
                    to={`/admin/departments/${department.id}`}
                    title="General"
                    icon={<i className="far fa-globe" />}
                  >
                    Department contact information & details.
                    <div className="pt-1 text-xs text-empty">
                      <i className="far fa-check text-success" /> Modified{" "}
                      {departmentUpdated} day{departmentUpdated !== 1 && "s"}{" "}
                      ago
                    </div>
                  </SettingsButton>
                </div>
              </div>
            )}

            <div className="mb-8">
              <h2>{department.title} Customer Information</h2>

              <div className="grid grid-cols-4 gap-4 sm:grid-cols-1">
                <SettingsButton
                  to={`/admin/settings/${department.id}/student-info-requests`}
                  title="Info Requests"
                  icon={<i className="far fa-circle-info" />}
                >
                  Non-critical information, like insurance info & t-shirt sizes.
                </SettingsButton>
              </div>
            </div>

            <div className="mb-8">
              <h2>{department.title} Operations</h2>

              <div className="grid grid-cols-4 gap-4 sm:grid-cols-1">
                <SettingsButton
                  to={`/admin/settings/${department.id}/programs`}
                  title="Programs"
                  icon={<i className="far fa-sitemap" />}
                >
                  Categories of things you offer for registration.
                </SettingsButton>

                <SettingsButton
                  to={`/admin/settings/${department.id}/tags`}
                  title="Tags"
                  icon={<i className="far fa-tags" />}
                >
                  An extra way to filter your offerings.
                </SettingsButton>

                {/* <SettingsButton
                  to={`/admin/settings/${department.id}/communications`}
                  title="Custom Emails"
                  icon={<i className="far fa-messages" />}
                >
                  Welcome email, registration receipt, and more.
                </SettingsButton> */}

                <SettingsButton
                  to={`/admin/settings/${department.id}/filters`}
                  title="Public Filtering"
                  icon={<i className="far fa-filter" />}
                >
                  Keep registration simple for your customers.
                </SettingsButton>

                <SettingsButton
                  to={`/admin/settings/${department.id}/billing-categories`}
                  title="Billing Categories"
                  icon={<i className="far fa-file-invoice-dollar" />}
                >
                  Internally group transactions for your reporting.
                </SettingsButton>
              </div>
            </div>

            <div className="mb-8">
              <h2>{department.title} Customer Checkout Options</h2>
              <div className="grid grid-cols-4 gap-4 sm:grid-cols-1">
                <SettingsButton
                  to={`/admin/settings/${department.id}/discounts`}
                  title="Discounts"
                  icon={<i className="far fa-hand-holding-dollar" />}
                >
                  Auto-apply multi-participant and multi-registration discounts.
                </SettingsButton>

                <SettingsButton
                  to={`/admin/settings/${department.id}/coupons`}
                  title="Coupons"
                  icon={<i className="far fa-gift-card" />}
                >
                  Add codes and details.
                </SettingsButton>

                <SettingsButton
                  to={`/admin/settings/${department.id}/addons`}
                  title="Products, Add-ons"
                  icon={<i className="far fa-cash-register" />}
                >
                  Up-sell anything during registrations & in{" "}
                  {store.organization.title} Store.
                </SettingsButton>
              </div>
            </div>
          </Container>
        </div>
      </Container>
    </>
  );
}
